import { useState } from 'react';
import { useWizard } from '../wizardContext';
import { SUCCESS_WITHOUT_SUBMIT_TITLE } from '../types';
import { fmtBRL, maskCPFout } from '../utils/finance';
import { resolveRedirectHref, resolveRedirectTarget } from '../utils/redirect';
import { sanitizeHtml } from '../../../utils/sanitize';
import { CaretIcon, CheckIcon, ShieldIcon } from '../parts/icons';

interface Props {
  onSubmit: () => void;
  onRestart: () => void;
}

const PREF_LABELS: Record<string, string> = { email: 'E-mail', whatsapp: 'WhatsApp', telefone: 'Telefone' };

export const Step5Resumo = ({ onSubmit, onRestart }: Props) => {
  const { state, prev, result, config } = useWizard();
  const [expanded, setExpanded] = useState(false);
  const r = result;
  const p = state.personal;
  const s = state.simulation;
  const trade = state.hasTradeIn;

  const cta = config.ctaButton;
  const ctaEl = cta?.isActive ? (
    <a
      className="lpfs-btn lpfs-btn-primary"
      href={resolveRedirectHref(cta.redirect) || '#'}
      target={resolveRedirectTarget(cta.redirect)}
      rel={resolveRedirectTarget(cta.redirect) === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {cta.label}
    </a>
  ) : null;

  const showSuccess = state.submitted || !config.submitEnabled;
  const successTitle = config.submitEnabled ? 'Proposta enviada com sucesso!' : SUCCESS_WITHOUT_SUBMIT_TITLE;

  return (
    <div className="lpfs-anim-in">
      <div className="lpfs-step-head">
        <h2 className="lpfs-step-title">Resultado e resumo</h2>
        <p className="lpfs-step-sub">Confira a condição estimada para o seu financiamento.</p>
      </div>

      <div className="lpfs-trust">
        <ShieldIcon />
        <span>Simulação sem compromisso</span>
      </div>

      <div className="lpfs-result">
        <div className="lpfs-result-hero">
          <span className="lpfs-result-label">Parcela estimada</span>
          <span className="lpfs-result-installment">
            {r.term}x de {fmtBRL(r.installment)}
            <sup>*</sup>
          </span>
        </div>
        <dl className="lpfs-result-grid">
          <div className="lpfs-dl-row">
            <span>Valor do veículo</span>
            <strong>{fmtBRL(r.vehiclePrice)}</strong>
          </div>
          <div className="lpfs-dl-row">
            <span>Entrada</span>
            <strong>{fmtBRL(r.downPayment)}</strong>
          </div>
          {r.tradeInValue > 0 && (
            <div className="lpfs-dl-row">
              <span>Seminovo na negociação</span>
              <strong>{fmtBRL(r.tradeInValue)}</strong>
            </div>
          )}
          <div className="lpfs-dl-row">
            <span>Valor financiado</span>
            <strong>{fmtBRL(r.financed)}</strong>
          </div>
          <div className="lpfs-dl-row">
            <span>Prazo</span>
            <strong>{r.term}x</strong>
          </div>
        </dl>
      </div>

      <div className="lpfs-fieldset" style={{ marginTop: 6 }}>
        <button
          type="button"
          className={`lpfs-expand-btn${expanded ? ' is-open' : ''}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Ocultar resumo' : 'Ver resumo completo'} <CaretIcon />
        </button>
        {expanded && (
          <div className="lpfs-summary-grid">
            <div className="lpfs-summary-card">
              <p className="lpfs-sc-title">Meus dados</p>
              <div className="lpfs-sc-rows">
                <div className="lpfs-sc-row">
                  <span>Nome</span>
                  <strong>{p.name}</strong>
                </div>
                <div className="lpfs-sc-row">
                  <span>E-mail</span>
                  <strong>{p.email}</strong>
                </div>
                <div className="lpfs-sc-row">
                  <span>Telefone</span>
                  <strong>{p.phone}</strong>
                </div>
                {config.showCpf && !!p.cpf && (
                  <div className="lpfs-sc-row">
                    <span>CPF</span>
                    <strong>{maskCPFout(p.cpf)}</strong>
                  </div>
                )}
                <div className="lpfs-sc-row">
                  <span>Contato por</span>
                  <strong>{p.contactPrefs.map((x) => PREF_LABELS[x] || x).join(', ')}</strong>
                </div>
              </div>
            </div>
            <div className="lpfs-summary-card">
              <p className="lpfs-sc-title">Entrada e troca</p>
              <div className="lpfs-sc-rows">
                <div className="lpfs-sc-row">
                  <span>Entrada</span>
                  <strong>{fmtBRL(s.downPayment)}</strong>
                </div>
                {trade ? (
                  <>
                    <div className="lpfs-sc-row">
                      <span>Seminovo</span>
                      <strong>{`${state.tradeIn.brand} ${state.tradeIn.model} ${state.tradeIn.year}`}</strong>
                    </div>
                    <div className="lpfs-sc-row">
                      <span>Valor do seminovo</span>
                      <strong>{fmtBRL(s.tradeInValue)}</strong>
                    </div>
                  </>
                ) : (
                  <div className="lpfs-sc-row">
                    <span>Seminovo</span>
                    <strong>Não utilizado</strong>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {!!config.summaryLegalText && (
        <div className="lpfs-legal" data-af-field="summaryLegalText" dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.summaryLegalText) }} />
      )}

      {!showSuccess ? (
        <div className="lpfs-actions">
          <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={prev}>
            Voltar
          </button>
          <span className="lpfs-spacer" />
          <button type="button" className="lpfs-btn lpfs-btn-primary" onClick={onSubmit}>
            Enviar proposta
          </button>
        </div>
      ) : (
        <>
          <div className="lpfs-success">
            <span className="lpfs-success-icon">
              <CheckIcon />
            </span>
            <div>
              <p className="lpfs-success-title">{successTitle}</p>
              {config.submitEnabled && (
                <p className="lpfs-success-text">
                  Em alguns instantes um de nossos consultores entrará em contato com você.
                </p>
              )}
            </div>
          </div>
          <div className="lpfs-actions">
            {!config.submitEnabled && (
              <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={prev}>
                Voltar
              </button>
            )}
            <span className="lpfs-spacer" />
            <button type="button" className="lpfs-btn lpfs-btn-ghost" onClick={onRestart}>
              Refazer simulação
            </button>
            {ctaEl}
          </div>
        </>
      )}
    </div>
  );
};
